import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { FilterChips } from '~/components/FilterChips';
import { Table } from '~/components/Table';
import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { usePagination } from '~/hooks/usePagination';
import { env } from '~/lib/env';
import { useUser } from '~/modules/auth/hooks/useUser';
import { FilterDrawer } from '~/modules/settings/components/FilterList';
import { DEFAULT_PAGE_SIZE } from '~/modules/settings/constants';
import { USER_TYPES } from '~/modules/settings/constants/Filter';
import { useFilters } from '~/modules/settings/hooks/useFilters';
import { useSearch } from '~/modules/settings/hooks/useSearch';
import { UsersFilterForm } from '~/modules/settings/modules/user/components/UserFormFilter/UserFormFilter';
import type { UserListItem } from '~settings/api/lib/schemas';
import { useUserList } from '~settings/api/queries/userList';
import { ListControls } from '~settings/components/ListControls';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { useDefaultSort } from '~settings/hooks/useDefaultSort';
import { translateOptions } from '~settings/utils/helpers';
import { RolesPriority } from '~settings/utils/roles.ts';

import { useUsersColumns } from './hooks/useUsersColumns';

const userEmptyArray: UserListItem[] = [];

const DEFAULT_INTERNAL_FILTER = { isActive: 'true' };

export function ListUserPage() {
  const { hasPermission } = useUser();
  const manageExternalUserEnabled =
    env('VITE_CONTAINER_SETTINGS_MANAGE_EXTERNAL_USER_ENABLED', false) ===
    'true';

  const canCreateUser =
    hasPermission('USER.CREATE') && manageExternalUserEnabled;

  const { page, setPageNumber, perPage, skip, take } =
    usePagination(DEFAULT_PAGE_SIZE);
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { searchTerm } = useSearch();
  const isCurrentUserInternal = !useIsExternalUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const { filterData, drawerOpen, closeDrawer, openDrawer } = useFilters(
    isCurrentUserInternal ? DEFAULT_INTERNAL_FILTER : {},
  );

  const columns = useUsersColumns(filterData);
  const { sorting, setSorting, orderBy } = useDefaultSort([
    { id: 'fullName', desc: false },
  ]);

  const { isLoading, isFetching, isError, userList } = useUserList(
    {
      skip,
      take,
      ...(orderBy && { orderBy }),
      ...filterData,
      ...(searchTerm && { search: searchTerm }),
    },
    {
      select: (data) => ({
        ...data,
        data: data.data.map((user) => ({
          ...user,
          // parent label with the highest priority role 1st
          labels: [...user.labels].sort((labelA, labelB) => {
            const result =
              Number(labelB.isParentLabel) - Number(labelA.isParentLabel);

            if (!result) {
              // since roles are sorted by priority already(in schema),
              // show label with higher priority role 1st
              return (
                RolesPriority[labelA.roles[0]] - RolesPriority[labelB.roles[0]]
              );
            }

            return result;
          }),
        })),
      }),
    },
  );

  useEffect(() => {
    if (isCurrentUserInternal && !searchParams.size) {
      setSearchParams(new URLSearchParams(DEFAULT_INTERNAL_FILTER));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once page loads for internal user

  const onClick = (row: UserListItem) => {
    navigate(SETTINGS_ROUTES.USER_DETAIL(row.id));
  };

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (isError) {
    // eslint-disable-next-line i18next/no-literal-string
    return <section className="py-md">Something went wrong.</section>;
  }

  return (
    <>
      <ListControls
        placeholder={t('user.search')}
        total={userList?.total ?? 0}
        page={page}
        perPage={perPage}
        entity="users"
        onFilterClick={openDrawer}
        setPageNumber={setPageNumber}
        toolbarRightAddon={
          canCreateUser ? (
            <Button
              asChild
              variant="primary"
              className="shrink-0 self-center"
              size="large"
            >
              <Link to={SETTINGS_ROUTES.USER_CREATE}>{t('user.addLink')}</Link>
            </Button>
          ) : null
        }
        internalFilter={
          <FilterChips
            options={translateOptions(USER_TYPES, t)}
            path="isInternal"
          />
        }
      />
      <Table
        data={userList?.data ?? userEmptyArray}
        columns={columns}
        onRowClick={onClick}
        sort={{
          sorting,
          onSortingChange: setSorting,
        }}
        skeletonLoading={{
          isLoading: isFetching,
          prefillRowsNumber: DEFAULT_PAGE_SIZE,
        }}
        rowClassName="h-3xl"
        cellClassName="p-sm"
      />
      <FilterDrawer open={drawerOpen} onOpenChange={closeDrawer}>
        <UsersFilterForm open={drawerOpen} onApply={closeDrawer} />
      </FilterDrawer>
    </>
  );
}
ListUserPage.displayName = 'ListUserPage';
